<template>
  <div>
    <b-row class="card-body">
      <b-col cols="12" md="10">
        <label>{{ 'invoice' | translate('global') }}</label>
        <b-form-group>
          <treeselect value-format="object" :default-expand-level="1"
                      :placeholder="'invoice_select' | translate('global')"
                      instanceId="loadInvoices" :load-options="loadOptions" :async="true" :cacheOptions="false"
                      :searchPromptText="'invoice_autocomplete' | translate('global')"
                      :noResultsText="'no_results_found' | translate('global')"
                      v-model="selectedInvoice"/>
        </b-form-group>
      </b-col>
      <b-col cols="12" md="2">
        <div class="pull-right">
          <b-button variant="primary"
                    class="mt-md-29"
                    target="_blank"
                    :to="{ name: 'InvoiceCreate', query: { supplier: this.selectedSupplier, structure: this.selectedStructure } }">
            {{ 'invoices_create_new' | translate('global') }}
          </b-button>
        </div>
      </b-col>
      <b-col cols="12">
        <div v-if="contentIsLoading" class="text-center mt-5 mb-5">
          <b-spinner type="grow" variant="primary"/>
        </div>
        <div v-else>
          <EmptyTable v-if="!tableItems.length"/>
          <div v-else class="table-responsive">
            <table class="table table-bordered table-sm mt-3 text-center">
              <thead>
              <tr>
                <th>R</th>
                <th class="border-r-3">F</th>
                <th colspan='4' class="border-r-3">{{ 'order_basic_info' | translate('global') }}</th>
                <th colspan='3' class="border-r-3">{{ 'fact' | translate('global') }}</th>
                <th colspan='3' class="border-r-3">{{ 'invoice_basic_info' | translate('global') }}</th>
                <th colspan='3' class="outside-r-border">{{ 'options' | translate('global') }}</th>
              </tr>
              </thead>

              <tbody>
              <tr class="table-secondary">
                <td colspan="1"><b-check :disabled="!selectedInvoice || updateDisable" v-model="allInvoices" /></td>
                <td colspan="1" class="border-r-3"><b-check :disabled="updateDisable" v-model="allFacts"/></td>

                <td colspan="1"><small>{{ 'product_service' | translate('global') }}</small></td>
                <td colspan="1"><small>{{ 'amount' | translate('global') }}</small></td>
                <td colspan="1"><small>{{ 'price' | translate('global') }}</small></td>
                <td colspan="1" class="border-r-3"><small>{{ 'sum' | translate('global') }}</small></td>

                <td colspan="1"><small>{{ 'amount' | translate('global') }}</small></td>
                <td colspan="1"><small>{{ 'price' | translate('global') }}</small></td>
                <td colspan="1" class="border-r-3"><small>{{ 'sum' | translate('global') }}</small></td>

                <td colspan="1"><small>{{ 'supplier' | translate('global') }}</small></td>
                <td colspan="1"><small>{{ 'invoice_number' | translate('global') }}</small></td>
                <td colspan="1" class="border-r-3"><small>{{ 'invoice_issue_at' | translate('global') }}</small></td>
                <td class="outside-r-border"></td>
              </tr>
              <tr v-for="(item, key) in tableItems" :key="key" :class="{ 'table-primary' : activeFactId === item.id }">
                <td>
                  <b-check :disabled="!selectedInvoice || !invoices[item.id].assignAction"
                           v-model="invoices[item.id].value"/>
                </td>
                <td class="border-r-3">
                  <b-check  :disabled="!item.actions['update_fact']" v-model="facts[item.id]"/>
                </td>

                <td colspan="1">{{ item['product'] }}</td>
                <td colspan="1">{{ item['amount'] }}</td>
                <td colspan="1">{{ item['price'] }}</td>
                <td colspan="1" class="border-r-3">{{ item['sum'] }}</td>

                <template v-if="activeFactId !== item.id">
                  <td colspan="1">{{ item['factAmount'] }}</td>
                  <td colspan="1">{{ item['factPrice'] }}</td>
                  <td colspan="1" class="border-r-3">{{ item['factSum'] }}</td>
                </template>

                <template v-else>
                  <td colspan="1">
                    <b-form-group>
                      <b-form-input type="number" min="0" class="min-w-100 text-center"
                                    :class="{ 'is-invalid' : errorMsg.fact_quantity }"
                                    v-model="updateFactData.fact_quantity"/>
                      <div class="invalid-feedback" v-if="errorMsg.fact_quantity">{{ errorMsg.fact_quantity[0] }}</div>
                    </b-form-group>
                  </td>
                  <td colspan="1">
                    <b-form-group>
                      <b-form-input type="number" min="0" class="min-w-100 text-center"
                                    :class="{ 'is-invalid' : errorMsg.fact_price }"
                                    v-model="updateFactData.fact_price"/>
                      <div class="invalid-feedback" v-if="errorMsg.fact_price">{{ errorMsg.fact_price[0] }}</div>
                    </b-form-group>
                  </td>
                  <td colspan="1" class="border-r-3">
                    {{ (Number(updateFactData.fact_quantity) * Number(updateFactData.fact_price)).toFixed(2) }}
                  </td>
                </template>

                <td colspan="1">{{ item['supplier'] }}</td>
                <td colspan="1">{{ item['invoiceNumber'] }}</td>
                <td colspan="1" class="border-r-3">{{ item['invoiceIssueAt'] }}</td>
                <td class="outside-r-border">
                  <template v-if="item.actions['update_fact']">
                    <template v-if="activeFactId === item.id">
                      <i class="fa fa-floppy-o fa-lg fa-success mr-3" @click="updateFact()"/>
                      <i class="fa fa-times fa-lg fa-danger" @click="activeFactId = null"/>
                    </template>
                    <template v-else>
                      <i class="fa fa-edit fa-lg fa-primary" @click="onSelectRow(item)"/>
                    </template>
                  </template>
                  <template v-else>
                    <i class="fa fa-edit fa-lg fa-secondary-disabled"/>
                  </template>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </b-col>
      <b-col cols="12">
        <div class="pull-right">
          <b-button type="submit" variant="success" class="mt-3"
                    :disabled="spinners.isSaving || updateDisable" @click="onSave()">
            <b-spinner v-if="spinners.isSaving" type="grow" small/>
            {{ 'save' | translate('global') }}
          </b-button>
        </div>
      </b-col>
      <b-col cols="12" v-if="invoicesTableData.length">
        <InvoicesTable :data="invoicesTableData"/>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import statuses from '@/store/statuses';
import forEach from 'lodash.foreach';
import VuexTypes from '@/store/types/index';
import Services from '@/services/index';
import TreeselectMixin from '@/mixins/TreeselectMixin';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import EmptyTable from '@/views/sharedComponents/EmptyTableData';
import InvoicesTable from './InvoicesTable.vue';

const OrdersTypes = VuexTypes.orders;
const OrdersService = Services.orders;

export default {
  mounted() {
    this.getData();
  },
  mixins: [TreeselectMixin],
  data() {
    return {
      spinners: {
        isSaving: false,
      },
      updateDisable: true,
      tableItems: [],
      invoices: {},
      facts: {},
      selectedInvoice: null,
      allInvoices: null,
      allFacts: null,
      errorMsg: {},
      activeFactId: null,
      updateFactData: {
        fact_quantity: null,
        fact_price: null,
      },
      invoicesTableData: [],
      selectedSupplier: null,
      selectedStructure: null
    };
  },
  components: { Treeselect, EmptyTable, InvoicesTable },
  computed: {
    ...mapGetters('Orders', [
      'orderUpdateData',
      'orderApproval',
      'orderFactsAndInvoicesList',
      'orderUpdateFact',
      'orderApproveFactAndInvoice',
      'orderItemsSendInvoiceToBookkeeper',
      'orderItemsApproveInvoiceReview',
      'orderItemsDeclineInvoiceReview'
    ]),
    contentIsLoading() {
      if (this.orderFactsAndInvoicesList.status === statuses.LOADED) {
        return false;
      }
      return true;
    }
  },
  watch: {
    orderUpdateData: {
      handler: function () {
        if (this.orderUpdateData.status === statuses.LOADED) {
          this.selectedSupplier = this.orderUpdateData.data.order.supplier.id;
          this.selectedStructure = this.orderUpdateData.data.order.structure.id;
        }
      },
      deep: true,
      immediate: true
    },
    orderApproval: {
      handler: function () {
        if (this.orderApproval.status === statuses.LOADED) {
          this.getData();
        }
      },
      deep: true
    },
    orderFactsAndInvoicesList: {
      handler: function () {
        if (this.orderFactsAndInvoicesList.status === statuses.LOADED) {
          this.allFacts = null;
          this.allInvoices = null;
          this.fillData();
        }
      },
      deep: true
    },
    orderUpdateFact: {
      handler: function () {
        if (this.orderUpdateFact.status === statuses.LOADED) {
          this.getData();
          this.alertSuccess();
          this.activeFactId = null;
          this.updateFactData['fact_quantity'] = null;
          this.updateFactData['fact_price'] = null;
        } else if (this.orderUpdateFact.status === statuses.ERROR) {
          this.alertError();
          this.errorMsg = this.orderUpdateFact.data.data.errors;
        }
      },
      deep: true
    },
    orderApproveFactAndInvoice: {
      handler: function () {
        if (this.orderApproveFactAndInvoice.status === statuses.LOADED) {
          this.getData();
          this.alertSuccess();
        } else if (this.orderUpdateFact.status === statuses.ERROR) {
          this.alertError();
          this.errorMsg = this.orderApproveFactAndInvoice.data.data.errors;
        }
      },
      deep: true
    },
    orderItemsSendInvoiceToBookkeeper: {
      handler: function () {
        if (this.orderItemsSendInvoiceToBookkeeper.status === statuses.LOADED) {
          this.selectedInvoice = null;
        }
      },
      deep: true
    },
    orderItemsApproveInvoiceReview: {
      handler: function () {
        if (this.orderItemsApproveInvoiceReview.status === statuses.LOADED) {
          this.selectedInvoice = null;
          this.getData();
        }
      },
      deep: true
    },
    orderItemsDeclineInvoiceReview: {
      handler: function () {
        if (this.orderItemsDeclineInvoiceReview.status === statuses.LOADED) {
          this.selectedInvoice = null;
          this.getData();
        }
      },
      deep: true
    },
    selectedInvoice: function () {
      if (typeof this.selectedInvoice !== 'undefined') {
        this.getData();
      }
    },
    allFacts: function () {
      if (this.allFacts !== null) {
        forEach(this.facts, (value, id) => {
          this.facts[id] = !!this.allFacts;
        });
      }
    },
    allInvoices: function () {
      if (this.allInvoices !== null) {
        forEach(this.invoices, (data, id) => {
          if (data.assignAction) {
            this.invoices[id]['value'] = !!this.allInvoices;
          }
        });
      }
    }
  },
  methods: {
    ...mapActions('Orders', [
      OrdersTypes.ORDER_FACTS_AND_INVOICES_LIST,
      OrdersTypes.ORDER_UPDATE_FACT,
      OrdersTypes.ORDER_APPROVE_FACT_AND_INVOICE,
    ]),
    getData() {
      const data = {
        orderId: this.$route.params.id,
      };
      if (this.selectedInvoice && this.selectedInvoice['id']) {
        data['invoiceId'] = this.selectedInvoice['id'];
      }
      this.ORDER_FACTS_AND_INVOICES_LIST(data);
    },
    fillData() {
      this.invoicesTableData = this.orderFactsAndInvoicesList.data.invoices;
      let updateDisable = true;
      const result = [];
      const facts = {};
      const invoices = {};

      forEach(this.orderFactsAndInvoicesList.data.facts, (item) => {
        result.push({
          id: item['id'],
          product: item['product'] ? item['product']['full_name'] : null,
          amount: item['quantity'],
          price: item['price'],
          sum: item['total'],
          factAmount: item['fact_quantity'],
          factPrice: item['fact_price'],
          factSum: item['total_fact'],
          actions: item['actions'],
          supplier: item['invoice'] ? item['invoice']['supplier']['name'] : '-',
          invoiceNumber: item['invoice'] ? item['invoice']['number'] : '-',
          invoiceIssueAt: item['invoice'] ? this.$formatDate(item['invoice']['issue_at']) : '-',
        });
        facts[item['id']] = item['is_fact_approved'];
        invoices[item['id']] = {
          value: !!item['invoice'],
          assignAction: item['actions']['assign_invoice'],
        };

        if (item['actions']['update_fact'] || item['actions']['assign_invoice']) {
          updateDisable = false;
        }
      });

      this.tableItems = result;
      this.facts = facts;
      this.invoices = invoices;
      this.updateDisable = updateDisable;
    },
    updateFact() {
      this.ORDER_UPDATE_FACT({
        orderId: this.$route.params.id,
        itemId: this.activeFactId,
        updateData: this.updateFactData
      });
    },
    onSelectRow(item) {
      this.activeFactId = item.id;
      Vue.set(this.updateFactData, 'fact_quantity', item['factAmount']);
      Vue.set(this.updateFactData, 'fact_price', item['factPrice']);
      this.errorMsg = {};
    },
    onSave() {
      const approveData = {
        facts: [],
      };

      forEach(this.facts, (value, id) => {
        approveData['facts'].push({
          id: id,
          is_fact_approved: value ? 1 : 0,
        });
      });
      if (Object.keys(this.invoices).length && this.selectedInvoice && this.selectedInvoice.id) {
        approveData['invoice'] = {
          id: this.selectedInvoice.id,
          assignedItems: []
        };
        forEach(this.invoices, (data, id) => {
          if (data.value) {
            approveData['invoice']['assignedItems'].push(id);
          }
        });
      }
      this.ORDER_APPROVE_FACT_AND_INVOICE({
        orderId: this.$route.params.id,
        approveData: approveData,
      });
    },
    async loadOptions({ action, searchQuery, callback, instanceId }) {
      let options = [];
      const query = searchQuery.trim();
      if (action === 'ASYNC_SEARCH') {
        const valid = this.checkSearchQuery(query);
        if (valid) {
          options = await this[instanceId](query);
        }
      }
      callback(null, options);
    },
    async loadInvoices(name) {
      const param = name || '';
      const data = await OrdersService.searchInvoice(this.$route.params.id, param);
      const options = [];
      forEach(data.data.data, (item) => {
        options.push({
          id: item['id'],
          label: `${item['number']}, (
          ${this.$t('sum', 'global')} ${item['sum']},
          ${this.$t('issue_at', 'global')} ${item['issue_at']}
          )`
        });
      });
      return options;
    },
  }
};
</script>

<style>
  .border-r-3 {
    border-right-width: 3px !important;
    border-right-color: #c8ced3 !important;
  }
  .outside-r-border {
    border-right-color: #c8ced3 !important;
  }
  .outside-l-border {
    border-left-color: #c8ced3 !important;
  }
  .table-primary th, .table-primary td, .table-primary thead th, .table-primary tbody + tbody {
    border-color: #c8ced3;
  }
</style>
